import NoNotesCard from "./no-notes";

import PatientNote from "./patient-note";
import NoPreviewCard from "../no-preview-card";
import { ClipboardListIcon, PencilIcon } from "lucide-react";
import { PatientNotes, PatientNoteItem } from "../../../api/types";
import { ViewNotesModal } from "../../modals/view-notes-modal";
import useAddNoteStore from "../../../store/add-note-store";
import { Button } from "@workspace/web-ui/components/button";
import useEncounterStore from "../../../store/encounter-store";
import { useGenerateNotesMutation } from "../../../api/mutations";
import { toast } from "sonner";
import { queryClient } from "~/main";
import { useProgram } from "~/hooks/use-program";
import { useState } from "react";
import { ICD10Modal } from "../../modals/icd10-modal";
import { tasksKeys } from "~/features/dashboard/api/tasks/keys";

const AssignedDoctor = ({ name, date }: { name: string, date: string }) => {
    return (
        <div className="flex items-center">
            <span className="text-xs font-normal">{name}</span>
            <span className="mx-1 sm:mx-2 text-gray-400" aria-hidden="true">•</span>
            <span className="text-xs font-normal">{date}</span>
        </div>
    )
}

const NotesCard = ({ notesDataEncounter }: { notesDataEncounter: PatientNotes | null }) => {
    const { isNoteModalOpen, selectedNoteData, selectedPatientId, closeNoteModal, openNoteModal } = useAddNoteStore()
    const { setEncounterId, encounterId } = useEncounterStore()
    const generateNotesMutation = useGenerateNotesMutation();
    const { program } = useProgram();
    const [isGenerateFlow, setIsGenerateFlow] = useState(false);
    const [isICD10ModalOpen, setIsICD10ModalOpen] = useState(false);
    const [isRefetching, setIsRefetching] = useState(false);

    const handleGenerateNotes = () => {
        setIsGenerateFlow(true);
        setIsICD10ModalOpen(true);
    }

    const handleICD10Submit = async (data: { icd10_code?: string }) => {            // Generate notes with ICD10 code
        const currentEncounterId = notesDataEncounter?.notes?.[0]?.encounter_id || encounterId || '';
        const currentPatientId = selectedPatientId || '';
        
        generateNotesMutation.mutate(
            { encounterId: currentEncounterId, patientId: currentPatientId, icd10_code: data.icd10_code, program },
            {
                onSuccess: async () => {
                    toast.success("Notes generated successfully!");
                    setIsRefetching(true);
                    // Wait for the query to refetch before closing the modal
                    try {
                        await queryClient.refetchQueries({
                            queryKey: tasksKeys.getTasksByEncounterId(currentEncounterId),
                        });
                        setIsICD10ModalOpen(false);
                        setIsGenerateFlow(false);
                    } catch (error) {
                        // Even if refetch fails, close the modal after a short delay
                        setTimeout(() => {
                            setIsICD10ModalOpen(false);
                            setIsGenerateFlow(false);
                        }, 500);
                    } finally {
                        setIsRefetching(false);
                    }
                },
                onError: () => {
                    toast.error("Failed to generate notes");
                },
            }
        );
    };

    return (
        <>
            <div className="flex flex-col gap-4 bg-white rounded-lg border border-[#E6E6E6] shadow-sm w-full">
                {!notesDataEncounter?.notes?.length ? (
                    <NoPreviewCard
                        Icon={ClipboardListIcon}
                        title="No notes"
                        description="This patient does not have any notes or consultation yet."
                        cardTitle="Consultation Notes"
                    />
                ) : (
                    <>
                        <div className="px-6 py-4 flex gap-2 border-b border-[#DCDCDE] w-full">
                            <h2 className="text-lg font-medium text-[#2D2F35] text-left">Consultation Notes</h2>
                        </div>
                        <div className="flex flex-col px-6 py-4">
                            <div className="flex items-start gap-4">
                                <div className="flex flex-col gap-10 w-full">
                                    {notesDataEncounter?.notes?.map((note: PatientNoteItem, id: number) => (
                                        <div key={note.encounter_id || id} className="flex flex-col gap-2 w-full">
                                            <div className="flex items-start gap-2 w-full">
                                            <img src="/icons/timeline.svg" alt="timeline" />
                                                <div className="w-full">
                                                    {note.created_at && (
                                                        <div className="flex items-start justify-between w-full">
                                                            <AssignedDoctor
                                                                name=""
                                                                date={new Date(note.created_at).toLocaleDateString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: '2-digit',
                                                                })}
                                                            />
                                                            {note.content && (
                                                                <Button
                                                                    onClick={() => {
                                                                        openNoteModal(note.content, "")
                                                                        setEncounterId(note.encounter_id)
                                                                    }}
                                                                    className="rounded-md flex items-center gap-2"
                                                                >
                                                                    <PencilIcon className="size-3 md:size-4" />
                                                                    <span className="hidden md:inline">Update note</span>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    )}
                                                    <h2 className="text-base font-medium mb-4 text-[#2D2F35] mt-2">Consultation Notes</h2>
                                                    {note.content ? (
                                                        <div className="rounded-3xl shadow-sm p-2">
                                                            <PatientNote patientNote={note.content} />
                                                        </div>
                                                    ) : (
                                                        <NoNotesCard encounterId={note.encounter_id} patientId="" />
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
            
            {/* Single modal for all notes */}
            <ViewNotesModal 
                data={selectedNoteData} 
                patientId={selectedPatientId || ''} 
                open={isNoteModalOpen} 
                handleGenerateNotes={handleGenerateNotes}
                isPending={generateNotesMutation.isPending}
                onOpenChange={closeNoteModal} 
                onSubmit={handleICD10Submit}
                isLoading={generateNotesMutation.isPending || isRefetching}
                isGenerateFlow={isGenerateFlow}
            />

            <ICD10Modal
                open={isICD10ModalOpen}
                onOpenChange={(open) => {
                    setIsICD10ModalOpen(open);
                    if (!open) {
                        setIsGenerateFlow(false);
                    }
                }}
                onSubmit={handleICD10Submit}
                isLoading={generateNotesMutation.isPending || isRefetching}
                isGenerateFlow={isGenerateFlow}
            />
        </>
    )
}

export default NotesCard;